import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  beginConnect,
  disconnectAccount,
  fetchConnections,
  renameOrganization,
  setCalendarSelected,
  setReadDepth,
  type Connection,
  type ReadDepth,
  type SignedInOwner,
} from "../api.js";
import { ConnectionOutcome } from "./ConnectionOutcome.js";
import { AddCalendarLink } from "./AddCalendarLink.js";

const STATUS_LABEL: Record<Connection["status"], string> = {
  connected: "Connected",
  needs_admin_consent: "Needs approval",
  blocked_by_policy: "Blocked by policy",
  expired: "Expired",
  revoked: "Revoked",
};

function relativeTime(iso: string | null): string {
  if (!iso) return "not synced yet";
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${String(hours)} h ago`;
  return `${String(Math.round(hours / 24))} d ago`;
}

export function Connections(_props: { owner: SignedInOwner }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  /** The account whose label is being edited, and what it is being changed to. */
  const [renaming, setRenaming] = useState<{ id: string; label: string } | null>(null);

  const connections = useQuery({ queryKey: ["connections"], queryFn: fetchConnections });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["connections"] });

  const toggleCalendar = useMutation({
    mutationFn: ({ id, selected }: { id: string; selected: boolean }) =>
      setCalendarSelected(id, selected),
    onSuccess: invalidate,
  });
  const disconnect = useMutation({
    mutationFn: disconnectAccount,
    onSuccess: invalidate,
  });
  const changeDepth = useMutation({ mutationFn: setReadDepth, onSuccess: invalidate });
  // Accounts are labelled by their email domain, which is right often enough to
  // be a good default and wrong often enough to need changing - "mia.org" says
  // less than "Day job" when four organisations are stacked in one calendar.
  const rename = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) =>
      renameOrganization(id, label),
    onSuccess: () => {
      setRenaming(null);
      return invalidate();
    },
  });
  const data = connections.data;
  const accounts = data?.connections ?? [];
  const microsoftCount = accounts.filter((a) => a.provider === "microsoft").length;
  const googleCount = accounts.filter((a) => a.provider === "google").length;
  const linkCount = accounts.filter((a) => a.provider === "ics").length;
  const selectedCount = accounts.reduce(
    (total, account) => total + account.calendars.filter((c) => c.selected).length,
    0,
  );
  const lastSynced = accounts
    .map((a) => a.lastSyncedAt)
    .filter((value): value is string => value !== null)
    .sort()
    .at(-1);

  return (
    <section className="card wide">
      <h1>Connections</h1>

      <ConnectionOutcome microsoft={data?.microsoft} />

      {connections.isPending ? <p className="lede">Loading your connections…</p> : null}

      {accounts.length > 0 ? (
        <p className="lede">
          {accounts.length} {accounts.length === 1 ? "account" : "accounts"} ·{" "}
          {selectedCount} {selectedCount === 1 ? "calendar" : "calendars"} counted as busy
          · {microsoftCount}/5 Microsoft, {googleCount}/2 Google
          {linkCount > 0
            ? `, ${String(linkCount)} subscription${linkCount === 1 ? "" : "s"}`
            : ""}{" "}
          · <span className="mono">last synced {relativeTime(lastSynced ?? null)}</span>
        </p>
      ) : null}

      <div className="row">
        <button
          type="button"
          className="primary"
          onClick={() => void beginConnect("microsoft")}
        >
          Connect Office 365 Calendar
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() => void beginConnect("google")}
        >
          Connect Google Calendar
        </button>
        <AddCalendarLink />
      </div>

      {data ? (
        <div className="depth">
          <span>Reads</span>
          {(
            [
              ["free_busy_only", "Free/busy only"],
              ["titles_too", "Titles too"],
            ] as [ReadDepth, string][]
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={data.readDepth === value ? "chip chip-on" : "chip"}
              aria-pressed={data.readDepth === value}
              onClick={() => changeDepth.mutate(value)}
            >
              {label}
            </button>
          ))}
          <span className="helper">
            Read-only either way. Visitors only ever see available or not available.
          </span>
        </div>
      ) : null}

      {accounts.length === 0 && !connections.isPending ? (
        <div className="empty">
          <h2>Connect the calendars you want counted as busy</h2>
          <p className="lede">
            Up to five Microsoft 365 work or school accounts and two Google accounts —
            from as many organizations as you belong to.
          </p>
          <div className="promise">
            <div>
              <strong>Meet With does</strong>
              <ul>
                <li>Read event start and end times to find free slots</li>
                <li>Read which calendars exist so you can choose them</li>
                <li>
                  Show visitors only &ldquo;available&rdquo; or &ldquo;not
                  available&rdquo;
                </li>
              </ul>
            </div>
            <div>
              <strong>Never</strong>
              <ul>
                <li>Create, edit, move or delete events</li>
                <li>Send mail from your mailbox</li>
                <li>Read event bodies, attendees or attachments</li>
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      {accounts.map((account) => {
        const isOpen = expanded === account.id;
        return (
          <article key={account.id} className="account">
            <div className="account-head">
              <span
                className="source-dot"
                style={{
                  background: `var(--source-${String((account.calendars[0]?.colorIndex ?? 0) + 1)})`,
                }}
                aria-hidden="true"
              />
              <div className="account-id">
                {renaming?.id === account.id ? (
                  <form
                    className="rename"
                    onSubmit={(event) => {
                      event.preventDefault();
                      rename.mutate(renaming);
                    }}
                  >
                    <input
                      type="text"
                      required
                      maxLength={80}
                      autoFocus
                      aria-label="What to call this account"
                      value={renaming.label}
                      onChange={(event) => {
                        setRenaming({ id: account.id, label: event.target.value });
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") setRenaming(null);
                        // Enter saves explicitly rather than relying on the
                        // browser's implicit form submission.
                        if (event.key === "Enter") {
                          event.preventDefault();
                          rename.mutate(renaming);
                        }
                      }}
                    />
                    <button type="submit" className="ghost" disabled={rename.isPending}>
                      Save
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="rename-trigger"
                    title="Rename"
                    onClick={() => {
                      setRenaming({ id: account.id, label: account.organization });
                    }}
                  >
                    <strong>{account.organization}</strong>
                  </button>
                )}
                <span className="mono">{account.accountEmail}</span>
              </div>
              <span className="monogram" aria-hidden="true">
                {account.provider === "microsoft"
                  ? "M365"
                  : account.provider === "google"
                    ? "G"
                    : "ICS"}
              </span>
              <span className="count">
                {account.calendars.filter((c) => c.selected).length} of{" "}
                {account.calendars.length} selected
              </span>
              <span
                className={
                  account.status === "connected" ? "status" : "status status-warn"
                }
              >
                {STATUS_LABEL[account.status]}
              </span>
              <button
                type="button"
                className="ghost"
                onClick={() => void beginConnect(account.provider)}
              >
                Reconnect
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => disconnect.mutate(account.id)}
                disabled={disconnect.isPending}
              >
                Disconnect
              </button>
              <button
                type="button"
                className="ghost"
                aria-expanded={isOpen}
                onClick={() => setExpanded(isOpen ? null : account.id)}
              >
                {isOpen ? "▴" : "▾"}
              </button>
            </div>

            {isOpen ? (
              <div className="calendars">
                {account.calendars.map((calendar) => (
                  <label key={calendar.id} className="calendar">
                    <input
                      type="checkbox"
                      checked={calendar.selected}
                      onChange={(event) =>
                        toggleCalendar.mutate({
                          id: calendar.id,
                          selected: event.target.checked,
                        })
                      }
                    />
                    <span>{calendar.name}</span>
                    {calendar.isDefault ? (
                      <span className="helper">· default</span>
                    ) : null}
                  </label>
                ))}
                <p className="helper">
                  Only selected calendars count toward your busy time. Selections are
                  saved automatically.
                </p>
              </div>
            ) : null}
          </article>
        );
      })}
    </section>
  );
}
